import {
  Service,
  Resource,
  ServiceCategory,
  Organisation,
} from "@src/App/AppDataProvider/types";
import { Translatable } from "./types";
import getTranslatedValue from "../getTranslatedValue";
import { TFunction } from "react-i18next";
import { ServicesByCategory } from "@src/App/Steps/Services/StepContent/sortServicesByCategory/sortServicesByCategory";

const translatableMap: {
  [K in keyof DocumentMap]: (keyof DocumentMap[K])[];
} = {
  category: ["name"],
  resource: ["name", "description"],
  service: ["name", "description"],
  account: ["name", "bookingConfirmationMessage", "customNoAvailabilityText"],
};
type DocumentMap = {
  category: ServiceCategory | ServicesByCategory;
  resource: Resource;
  service: Service;
  account: Organisation;
};

type ToStringFields<T extends readonly string[]> = {
  [K in T[number]]: string;
};

/**
 * Builds a function that translates entities based on their type.
 *
 * @param {TFunction<string>} tCustomer - The translation function used to fetch localized values.
 * @returns {Function} A function that translates an entity into a standardized format.
 *
 * Translation keys follow the format: `<type>.<id>.<field>`.
 * If a translation does not exist for a given key, it falls back to the original (as stored in DB) field value.
 */
export function buildTranslateEntityFn(tCustomer: TFunction<string>) {
  function translateEntity<T extends keyof DocumentMap>(
    input: Translatable<T, DocumentMap[T]>,
  ): ToStringFields<(typeof translatableMap)[T]> {
    const baseKey = `${input.type}.${input.document._id}`;
    return Object.fromEntries(
      (translatableMap[input.type] as readonly (keyof DocumentMap[T])[]).map(
        (field) => [
          field,
          getTranslatedValue(baseKey, field, tCustomer, input.document),
        ],
      ),
    ) as ToStringFields<(typeof translatableMap)[T]>;
  }

  return translateEntity;
}
